// Week-6 cleanup of deferred Week-4/5 work.
// Shared-doc helpers for:
//   - portallink__<token>   — client-facing estimate portal link
//   - linkreq__<token>      — partner/client access link request
//
// Both doc shapes mirror the vanilla app writers (public/app/index.html),
// so v2 and vanilla round-trip on the same Firestore data.

import { setDoc, serverTimestamp } from 'firebase/firestore'
import { sharedDoc } from './firebase/firestore'
import { getShared, setShared } from './db/shared'
import { buildEstimateDisplayData } from './portalSync'
import type { Estimate, Client, Project, Opts } from '../types'

// ── Token generation ─────────────────────────────────────────────────────────

/** 48-char URL-safe random token — matches V1's portal.html token validation
 *  (public/portal/index.html:1647 rejects anything < 48 chars as "Invalid link").
 *  Two UUIDs concatenated (2 × 32 hex chars = 64), sliced to 48. */
export function generateToken(): string {
  const a = crypto.randomUUID().replace(/-/g, '')
  const b = crypto.randomUUID().replace(/-/g, '')
  return (a + b).slice(0, 48)
}

/** Portal host for client-facing links. When VITE_PORTAL_HOST is configured
 *  (e.g. https://jobsitepulse.app in prod), links target the public
 *  hosting:prod portal.html rather than the v2 SPA. Falls back to
 *  location.origin so local-dev and tests work without the env var. */
const PORTAL_HOST: string =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_PORTAL_HOST) ||
  (typeof location !== 'undefined' ? location.origin : '')

/** Portal URL the client will visit. Always targets the prod portal host
 *  (jobsitepulse.app) so the link opens public portal.html, not the v2 SPA. */
export function portalUrl(token: string): string {
  return `${PORTAL_HOST}/portal?token=${encodeURIComponent(token)}`
}

// ── Portal link (estimate → client) ──────────────────────────────────────────

export interface CreatePortalLinkInput {
  ownerUid: string
  estimate: Estimate
  client: Client | undefined
  /** When provided AND the estimate is linked to this project, the portal
   *  doc is created with status='accepted' so the portal page renders the
   *  project-portal dashboard (billing / draws / schedule / scope) instead
   *  of the estimate Accept/Decline view. V1 parity: sendToClientPortal
   *  sets status to 'accepted' whenever `t.projId && n` — public/app/
   *  index.html:21099-21100. */
  project?: Project | undefined
  /** Owner's options — passed through to biz branding fields on the
   *  portal doc (V1 L21158-21177) and to buildEstimateDisplayData for
   *  displayLines/displayTotal. */
  opts?: Opts | undefined
  /** Linked client's uid — when the client has a JSP account linked via a
   *  deallink__, pass their uid here. It is stamped on the portallink__ doc
   *  as `linkedClientUid` and included in `participantUids` so the client's
   *  `usePortalLinkForProject` array-contains query can find this doc. */
  linkedClientUid?: string | undefined
}

export interface PortalLinkResult {
  token: string
  url: string
  /** true when an existing portal token was reused (V1 parity: see the
   *  "Existing client portal link" vs "Client portal link created" branch
   *  in _showPortalLinkModal at public/app/index.html:21196). */
  reused?: boolean
}

/** Firestore rejects `undefined` at any depth. JSON round-trip drops
 *  undefined props cleanly — safe for plain data (our estimate/project
 *  snapshots contain no Date/Map/Set values). */
function stripUndefined<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

// V1 parity: sendToClientPortal (public/app/index.html:21093).
//
// Two paths:
//   A. Existing tokens on the estimate → fetch each, skip declined / wrong
//      owner; for the first reusable match, update the portal doc in place
//      (auto-promote pending→accepted when we have a project), return that
//      URL. V1 L21101-21138.
//   B. No reusable token → generate a new one, write the full V1 payload
//      (biz branding, portal customization, display lines, client info,
//      project linkage), return the new URL. V1 L21139-21194.
//
// The `status` field drives the Portal page UI:
//   'pending'  → Accept / Decline estimate view  (Portal.tsx L296)
//   'accepted' → project-portal dashboard        (same, gated on projId)
export async function createPortalLink({
  ownerUid, estimate, client, project, opts, linkedClientUid,
}: CreatePortalLinkInput): Promise<PortalLinkResult> {
  const clientName  = client?.name  || ''
  const clientEmail = client?.email ? client.email.toLowerCase() : ''
  // V1 L21099-21100: isProjectPortal when estimate is tied to a real project.
  const isProjectPortal = !!project && estimate.projId === project.id
  const status: 'accepted' | 'pending' = isProjectPortal ? 'accepted' : 'pending'
  const estName = estimate.name || project?.name || 'Project Estimate'

  // Display data — requires opts for margin calc. When not available fall
  // back to a conservative zero-state so the portal page doesn't crash.
  const display = opts
    ? buildEstimateDisplayData(estimate, opts)
    : { lines: [], displayTotal: 0, showMarginLine: false, margin: 0, hide: false }

  // ── Path A: reuse an existing token ────────────────────────────────────
  // V1 L21101-21138. Iterate `estimate.portalTokens`, stop at the first doc
  // that's alive + owned by us + not declined. Update its display + linkage,
  // auto-promote pending→accepted when we have a project.
  const existingTokens = Array.isArray(estimate.portalTokens) ? estimate.portalTokens : []
  for (const existingToken of existingTokens) {
    const docId = `portallink__${existingToken}`
    try {
      const existing = await getShared<{
        status?: string; ownerUid?: string; linkedClientUid?: string; participantUids?: string[]
      }>(docId)
      if (!existing) continue
      if (existing.ownerUid !== ownerUid) continue
      if (existing.status === 'declined') continue

      const update: Record<string, unknown> = {
        displayLines:   display.lines,
        displayTotal:   display.displayTotal,
        showMarginLine: display.showMarginLine,
        margin:         display.margin,
        hideLineItems:  display.hide,
        estName,
        clientName,
        clientEmail,
      }
      // V1 L21106-21114: pending → accepted promotion when we now have
      // project context.
      if (isProjectPortal && existing.status === 'pending' && project) {
        update.status       = 'accepted'
        update.projId       = project.id
        update.projName     = project.name
        update.respondedAt  = new Date().toISOString()
      } else if (isProjectPortal && project) {
        // Keep projId/projName fresh even if already accepted.
        update.projId   = project.id
        update.projName = project.name
      }
      // Stamp linkedClientUid + refresh participantUids if we now know the
      // client's uid but the existing doc doesn't have it yet.
      if (linkedClientUid !== undefined && existing.linkedClientUid === undefined) {
        update.linkedClientUid = linkedClientUid
        const existingParticipants = Array.isArray(existing.participantUids)
          ? existing.participantUids
          : [ownerUid]
        update.participantUids = existingParticipants.includes(linkedClientUid)
          ? existingParticipants
          : [...existingParticipants, linkedClientUid]
      }

      await setShared(docId, update)
      return { token: existingToken, url: portalUrl(existingToken), reused: true }
    } catch (err) {
      // V1 catches per-token errors silently and keeps iterating.
      console.warn('[portal] reuse check failed for token', existingToken, err)
    }
  }

  // ── Path B: create a new token ─────────────────────────────────────────
  const token = generateToken()
  const docId = `portallink__${token}`

  // V1 L21165-21174: bizDocs — only include when the user opted them into
  // the portal. Insurance and license each need the doc URL *and* the
  // *ShowPortal toggle.
  const bizDocs: Array<Record<string, unknown>> = []
  if (opts?.bizInsuranceDoc && opts.bizInsuranceShowPortal) {
    bizDocs.push({
      type: 'insurance',
      label: 'Certificate of Insurance',
      url: opts.bizInsuranceDoc,
      ...(opts.bizInsuranceExpDate ? { expDate: opts.bizInsuranceExpDate } : {}),
    })
  }
  if (opts?.bizLicenseDoc && opts.bizLicenseShowPortal) {
    bizDocs.push({ type: 'license', label: 'Contractor License', url: opts.bizLicenseDoc })
  }

  // Required keys per firestore.rules portallink__ create:
  //   keys().hasAll(['token', 'ownerUid', 'estId', 'status', 'sentAt'])
  // Everything else is extras that the Portal.tsx page / client-side portal
  // reads. Match V1 payload (L21143-21189) verbatim, conditionally including
  // optional strings so Firestore never sees `undefined`.
  const payload: Record<string, unknown> = {
    token,
    ownerUid,
    estId: estimate.id,
    estName,
    status,
    sentAt: new Date().toISOString(),

    // Project linkage (V1 L21151-21152)
    projId: project?.id ?? estimate.projId ?? null,
    projName: project?.name ?? null,

    // Estimate display (V1 L21153-21157)
    displayLines:   display.lines,
    displayTotal:   display.displayTotal,
    showMarginLine: display.showMarginLine,
    margin:         display.margin,
    hideLineItems:  display.hide,

    // Client (V1 L21178-21179)
    clientName,
    clientEmail,

    // Status lifecycle (V1 L21182-21184)
    respondedAt: isProjectPortal ? new Date().toISOString() : null,
    declineReason: null,

    // Misc (V1 L21185-21189)
    notes: '',
    messages: [],
    unreadContractorCount: 0,
    portalChangeOrders: [],

    // V2 extras: full estimate snapshot for clients to view line detail
    kind: 'estimate',
    estimateSnapshot: stripUndefined(estimate),

    // V1 L21180: default pay terms
    payTerms: opts?.defaultPayTerms || 'Due on receipt',

    // Biz branding (V1 L21158-21164)
    bizName:    opts?.bizName    || '',
    bizLogo:    opts?.bizLogo    || '',
    bizPhone:   opts?.bizPhone   || '',
    bizEmail:   opts?.bizEmail   || '',
    bizLicense: opts?.bizLicense || '',
    bizAddress: opts?.bizAddress || '',
    bizWebsite: opts?.bizWebsite || '',
    bizDocs,

    // Portal customization (V1 L21175-21177)
    portalGreeting:  opts?.portalGreeting  || '',
    portalSignature: opts?.portalSignature || '',
    portalAccent:    opts?.portalAccent    || '',

    // Participant read-path (see firestore.rules "PARTICIPANT READ").
    // Include linkedClientUid if provided so the client's array-contains
    // query in usePortalLinkForProject can find this doc immediately.
    participantUids: linkedClientUid !== undefined
      ? [ownerUid, linkedClientUid]
      : [ownerUid],

    // Audit timestamp
    _updatedAt: serverTimestamp(),
  }

  // Stamp linkedClientUid when provided. Conditional spread keeps the field
  // absent (not undefined) when not known, satisfying exactOptionalPropertyTypes.
  if (linkedClientUid !== undefined) payload.linkedClientUid = linkedClientUid

  if (estimate.expireDate) payload.expires = estimate.expireDate

  await setDoc(sharedDoc(docId), payload)

  return { token, url: portalUrl(token) }
}

// ── Link request (sender → recipient by email) ───────────────────────────────

/** V1 parity — role taxonomy used by link requests + deal links.
 *  'client' was added in V2 to support the primary GC→homeowner flow. */
export type LinkRole = 'investor' | 'gc' | 'sub' | 'client'

export const ROLE_LABELS: Record<LinkRole, string> = {
  investor: 'Investor/Owner',
  gc:       'GC/Contractor',
  sub:      'Subcontractor',
  client:   'Client',
}
export const ROLE_ICON: Record<LinkRole, string> = {
  investor: '🏦',
  gc:       '🔨',
  sub:      '🔧',
  client:   '🏠',
}

export interface CreateLinkRequestInput {
  fromUid: string
  fromEmail?: string
  fromName?: string
  /** V1 parity — sender's team code (upper-cased). Carried through so the
   *  receiver's approve flow can write the deallink__ partnerTeamCode. */
  fromTeamCode?: string
  /** V1 parity — sender's self-identified role in this relationship
   *  (public/app/index.html:9040). Receiver's modal reads this to render
   *  "Investor/Owner" etc. Without it the receiver shows "undefined". */
  fromRole?: LinkRole
  toEmail: string
  toPhone?: string
  message?: string
  /** Optional — attach client context so the receiver sees which relationship is being proposed. */
  clientId?: number
  clientName?: string
  /** Optional TTL in days. Default 30 (V1 parity). */
  expiresInDays?: number
}

export interface LinkRequestResult {
  token: string
  docId: string
}

export async function createLinkRequest(input: CreateLinkRequestInput): Promise<LinkRequestResult> {
  // V1 parity — public/app/index.html:9020 sendLinkRequest. V1's linkreq__
  // schema carries fromTeamCode + fromRole so the receiver's approve flow
  // can render "Investor/Owner" etc. and build the deallink__ payload.
  // Missing either field made the V1 receiver render "undefined" in the
  // role slot and the deallink__ write on approve was incomplete.
  const token = generateToken()
  const docId = `linkreq__${token}`

  // V1 uses ISO string for sentAt + 30-day expiry.
  const sentAt = new Date().toISOString()
  const ttlDays = input.expiresInDays ?? 30
  const expiresAt = new Date(Date.now() + ttlDays * 24 * 60 * 60 * 1000).toISOString()

  // Required keys per firestore.rules linkreq__ create:
  //   keys().hasAll(['fromUid', 'status', 'sentAt', 'expiresAt'])
  // Optional `message` must be <= 2000 chars.
  const toEmail = input.toEmail.trim().toLowerCase()
  const message = (input.message ?? '').slice(0, 2000)

  const payload: Record<string, unknown> = {
    token,
    fromUid: input.fromUid,
    toEmail,
    status: 'pending',
    sentAt,
    expiresAt,

    // V1 parity — null placeholders so the receiver's UI can read these
    // fields without hitting `undefined` before the approve writes update them.
    toUid: null,
    toName: null,
    toRole: null,
    respondedAt: null,
    dealLinkId: null,

    // Enables the participantUids / toEmail read paths in rules
    participantUids: [input.fromUid],

    _updatedAt: serverTimestamp(),
  }

  if (message)                  payload.message       = message
  if (input.fromEmail)          payload.fromEmail     = input.fromEmail.toLowerCase()
  if (input.fromName)           payload.fromName      = input.fromName
  if (input.fromTeamCode)       payload.fromTeamCode  = input.fromTeamCode.toUpperCase()
  if (input.fromRole)           payload.fromRole      = input.fromRole
  if (input.toPhone)            payload.toPhone       = input.toPhone
  if (input.clientId != null)   payload.clientId      = input.clientId
  if (input.clientName)         payload.clientName    = input.clientName

  await setDoc(sharedDoc(docId), payload)

  return { token, docId }
}

// ── Link request — approve / decline (receiver side) ────────────────────────
// V1 parity — public/app/index.html:9088 approveLinkRequest, :9155
// declineLinkRequest. Approve writes the linkreq update AND creates the
// deallink__ doc that powers cross-account sharing.

export interface ApproveLinkRequestInput {
  req: {
    id: string
    fromUid: string
    fromTeamCode?: string
    fromName?: string
    fromRole?: LinkRole
    participantUids?: string[]
  }
  /** Receiver's own uid (from useAuth) — NOT effectiveUid: the receiver
   *  saves the contact into their own appstate, and the rules check on
   *  linkreq update requires auth.uid matches one of from/to. */
  toUid: string
  /** Receiver's biz name — shown on sender's side as the partner name. */
  toName: string
  /** Receiver's team code (upper-cased) — carried through to deallink. */
  toTeamCode?: string
  /** Receiver's self-identified role. */
  toRole: LinkRole
}

export async function approveLinkRequest(input: ApproveLinkRequestInput): Promise<{ dealLinkId: string }> {
  const { req, toUid, toName, toTeamCode, toRole } = input
  const now = new Date().toISOString()
  const dealLinkId = `deallink__${Date.now()}`

  const existingParticipants = Array.isArray(req.participantUids) ? req.participantUids : []
  const participantUids = existingParticipants.includes(toUid)
    ? existingParticipants
    : [...existingParticipants, toUid]

  // 1. Update the linkreq__ doc — merge so we don't overwrite fields we
  //    don't know about (V1 adds toPhone etc. that V2 may not carry).
  await setShared(req.id, {
    toUid,
    toName,
    toRole,
    status: 'approved',
    respondedAt: now,
    dealLinkId,
    participantUids,
  })

  // 2. Create the deallink__ doc powering cross-account sharing.
  const dealPayload: Record<string, unknown> = {
    id: dealLinkId,
    initiatorUid: req.fromUid,
    partnerUid: toUid,
    status: 'active',
    createdAt: now,
    fromRequestId: req.id,
    participantUids: [req.fromUid, toUid].filter(Boolean),
  }
  if (req.fromTeamCode) dealPayload.initiatorTeamCode = req.fromTeamCode
  if (req.fromName)     dealPayload.initiatorName     = req.fromName
  if (req.fromRole)     dealPayload.side1Role         = req.fromRole
  if (toTeamCode)       dealPayload.partnerTeamCode   = toTeamCode
  if (toName)           dealPayload.partnerName       = toName
  if (toRole)           dealPayload.side2Role         = toRole

  await setShared(dealLinkId, dealPayload)

  return { dealLinkId }
}

export async function declineLinkRequest(reqId: string): Promise<void> {
  await setShared(reqId, {
    status: 'declined',
    respondedAt: new Date().toISOString(),
  })
}

// ── Share an estimate with a linked partner (V1 parity) ─────────────────────
// public/app/index.html:9552 — creates an estshare__ doc the partner reads
// via their estshare__ participantUids query. Bakes margin into amounts per
// V1 (9578–9590) so the partner sees the client-facing price, and sets
// noMargin=true on the snapshot so their view doesn't re-apply margin.

export interface ShareEstimateInput {
  dealLinkId: string
  fromUid: string
  fromName?: string
  fromTeamCode?: string
  toUid?: string | null
  toName?: string
  toTeamCode?: string
  estimate: {
    id?: number
    name?: string
    categories?: Array<{ name: string; items?: Array<{ desc?: string; amount?: number; qty?: number; cost?: number; price?: number }> }>
    noMargin?: boolean
  }
  marginPct?: number        // when > 0 and estimate.noMargin is false, bake into amounts
  message?: string
  /** Portal token to stamp on the estshare__ doc so the receiver can open
   *  the portal directly without a secondary lookup. When present the
   *  receiver's handleReview reads it straight off the doc. */
  portalToken?: string
}

export interface ShareEstimateResult { docId: string }

export async function shareEstimateWithPartner(input: ShareEstimateInput): Promise<ShareEstimateResult> {
  const docId = `estshare__${Date.now()}`
  const snap = JSON.parse(JSON.stringify(input.estimate)) as ShareEstimateInput['estimate']

  // Collapse categories to ones that have items with positive amounts.
  // V1 L9556 filter — partners should never receive empty placeholders.
  snap.categories = (snap.categories ?? [])
    .map((c) => ({ ...c, items: (c.items ?? []).filter((it) => {
      const amt = typeof it.amount === 'number' ? it.amount : (it.cost ?? 0) * (it.qty ?? 0)
      return amt > 0
    }) }))
    .filter((c) => (c.items ?? []).length > 0)
  if ((snap.categories ?? []).length === 0) {
    throw new Error('Add line items with amounts before sending — empty placeholders won\'t be sent')
  }

  // Bake margin into each item's amount so the partner sees client pricing.
  // IMPORTANT: do NOT round per-line. The sender stores project.budget as
  // Σ(base * (1 + pct/100)) — a single floating-point sum rounded only at
  // display time. The receiver derives its project.budget by summing the
  // same items from this snapshot (Estimates.tsx handleAcceptIncoming). If
  // we Math.round each line here, per-line rounding error accumulates and
  // the receiver's budget drifts $1–$N away from the sender's ("$3,045 vs
  // $3,046"). Keeping full precision on each amount guarantees both sides
  // round the same sum at the same step, so fmt() yields the same dollars.
  const marginPct = input.marginPct ?? 0
  if (marginPct > 0 && !input.estimate.noMargin) {
    for (const c of snap.categories ?? []) {
      for (const it of (c.items ?? [])) {
        const base = typeof it.amount === 'number' ? it.amount : (it.cost ?? 0) * (it.qty ?? 0)
        it.amount = base * (1 + marginPct / 100)
      }
    }
  }
  snap.noMargin = true

  const participantUids = [input.fromUid, input.toUid].filter(Boolean) as string[]
  const payload: Record<string, unknown> = {
    id: docId,
    linkId: input.dealLinkId,
    fromUid: input.fromUid,
    estimateSnapshot: snap,
    message: input.message ?? '',
    sentAt: new Date().toISOString(),
    status: 'pending',
    participantUids,
    toUid: input.toUid ?? null,
  }
  if (input.fromName)     payload.fromName     = input.fromName
  if (input.fromTeamCode) payload.fromTeamCode = input.fromTeamCode
  if (input.toName)       payload.toName       = input.toName
  if (input.toTeamCode)   payload.toTeamCode   = input.toTeamCode
  // Stamp the portal token so the receiver can open the portal directly.
  if (input.portalToken)  payload.portalToken  = input.portalToken

  await setShared(docId, payload)
  return { docId }
}
